// RadarOverviewPage — Technology Radar overview with 3 story cards + mini-plot previews
import { useState, useEffect } from 'preact/hooks'
import { RADAR_SECTIONS } from './radarConfig.js'
import StoryCard from './StoryCard.jsx'
import {
  fetchChemistryData,
  fetchPerformanceData,
  fetchNaionLfpData,
} from '../../services/radarService.js'
import { buildRagoneByCathode, buildCathodeMarketShare, buildNmcEvolution, buildRagoneByAnode, buildAnodeMarketShare } from './plots/chemistryPlots.js'
import { buildRagoneLowHighEnergy, buildEnergyDensityVsYear, buildPowerDensityVsYear } from './plots/performancePlots.js'
import { buildNaionLfpRagone, buildTemporalEvolution, buildKpiRadar } from './plots/naionLfpPlots.js'

/**
 * Overview page for the Technology Radar.
 * 3 story cards fill the entire viewport with mini-plot preview backgrounds.
 */
export default function RadarOverviewPage() {
  const [previews, setPreviews] = useState({})

  // Fetch data for mini-plot previews (non-blocking, fire-and-forget)
  useEffect(() => {
    let cancelled = false

    async function loadPreviews() {
      try {
        const [chemistry, performance, naionLfp] = await Promise.all([
          fetchChemistryData().catch(() => null),
          fetchPerformanceData().catch(() => null),
          fetchNaionLfpData().catch(() => null),
        ])

        if (cancelled) return

        const result = {}
        if (chemistry) {
          result['chemistry-evolution'] = [
            buildRagoneByCathode(chemistry, 'grav'),
            buildCathodeMarketShare(chemistry),
            buildNmcEvolution(chemistry),
            buildRagoneByAnode(chemistry, 'grav'),
            buildAnodeMarketShare(chemistry),
          ]
        }
        if (performance) {
          result['performance-limits'] = [
            buildRagoneLowHighEnergy(performance, 'grav'),
            buildEnergyDensityVsYear(performance),
            buildPowerDensityVsYear(performance),
          ]
        }
        if (naionLfp) {
          result['naion-vs-lfp'] = [
            buildNaionLfpRagone(naionLfp, 'grav'),
            buildTemporalEvolution(naionLfp),
            buildKpiRadar(naionLfp),
          ]
        }
        setPreviews(result)
      } catch {
        // Preview loading is best-effort — no error handling needed
      }
    }

    loadPreviews()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="radar-overview radar-overview--viewport">
      <div className="radar-overview__cards" data-tour="radar-overview-cards">
        {RADAR_SECTIONS.map((section) => (
          <StoryCard
            key={section.id}
            section={section}
            previewOption={previews[section.id] || null}
          />
        ))}
      </div>
    </div>
  )
}
